import { type ReactNode, useState } from "react";
import { basisLabel, fromNet, rateFor, type TaxRate, toNet, toNumber } from "../../shared/money";
import { TextField } from "../../web/components/fields";
import { InlinePicker } from "../../web/components/InlinePicker";
import { useMoney } from "../../web/lib/money";
import type { TaxQuote } from "./meta";

/*
 * An amount typed as it's quoted ("$22.00 inc GST"): a money field with how it's taxed picked
 * beside the amount, inside the field, and the same amount the other way under it ("= $20.00 ex
 * GST"). Amounts are stored before tax; this is only how they're typed and shown. Supplier prices,
 * a supplier's usual quoting, an item's typed cost and its price.
 *
 * The standard for every typed amount: switching how it's taxed keeps the number as typed and reads
 * it the new way ($22 ex GST becomes $22 inc GST), so the amount before tax changes. Prices never
 * reach ServiceM8 by themselves, so a switch shows as prices to update first. Only prices worked out
 * by a pricing rule keep their value instead (there's no typed number to keep).
 */

/** A quote's rate (blank: the account's default) and whether that rate charges anything. */
function resolve(quote: TaxQuote, rates: readonly TaxRate[]) {
    const rate = rateFor(quote.rateUuid, rates);
    return { rate, taxed: Boolean(rate && rate.percent > 0) };
}

/** A quote as one picker value: its rate, and inc or ex (an untaxed rate is just itself). */
export function quoteKey(quote: TaxQuote, rates: readonly TaxRate[]) {
    const { rate, taxed } = resolve(quote, rates);
    return `${rate?.uuid ?? ""}:${taxed && quote.includesTax ? "inc" : "ex"}`;
}

/** Every tax choice's label and note, the same wherever one's picked ("ex GST": "GST is added on top"). `tax`: its name ("GST", or "tax"). */
export const taxChoice = {
    ex: (tax: string) => ({ label: `ex ${tax}`, help: `${capitalise(tax)} is added on top` }),
    inc: (tax: string) => ({ label: `inc ${tax}`, help: `${capitalise(tax)} is already in it` }),
    none: (name: string) => ({ label: name, help: "No tax on it" }),
};
const capitalise = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);

/** The ways an amount can be taxed: ex and inc each taxed rate ("ex GST", "inc GST"), and each untaxed one once ("GST Free"), each with what it means. */
export function quoteOptions(rates: readonly TaxRate[]) {
    return rates.flatMap((r) =>
        r.percent > 0
            ? [
                  { value: `${r.uuid}:ex`, ...taxChoice.ex(r.name), quote: { rateUuid: r.uuid, includesTax: false } },
                  { value: `${r.uuid}:inc`, ...taxChoice.inc(r.name), quote: { rateUuid: r.uuid, includesTax: true } },
              ]
            : [{ value: `${r.uuid}:ex`, ...taxChoice.none(r.name), quote: { rateUuid: r.uuid, includesTax: false } }],
    );
}

/**
 * How an amount is taxed, as a small menu button (inside an amount field, where its "inc GST" label
 * would be): each way it can be taxed, with what that means, the current one ticked. Locked: just
 * the label.
 */
export function QuotePicker(
    props: Readonly<{
        rates: readonly TaxRate[];
        quote: TaxQuote;
        label: string;
        disabled?: boolean;
        onChange: (quote: TaxQuote) => void;
    }>,
) {
    const options = quoteOptions(props.rates);
    return (
        <InlinePicker
            label={props.label}
            options={options}
            value={quoteKey(props.quote, props.rates)}
            fallback={quoteLabel(props.quote, props.rates)}
            disabled={props.disabled}
            onChange={(key) => {
                const picked = options.find((o) => o.value === key);
                if (picked) props.onChange(picked.quote);
            }}
        />
    );
}

/** How an amount is quoted, for beside it: "inc GST", "ex GST", or an untaxed rate's name ("GST Free"). */
export function quoteLabel(quote: TaxQuote, rates: readonly TaxRate[]) {
    const { rate, taxed } = resolve(quote, rates);
    return taxed ? basisLabel(quote.includesTax, rate) : (rate?.name ?? "no tax");
}

/** The same amount the other way, with or without its tax ("= $20.00 ex GST"), or null when there's no tax to differ by. */
export function otherWay(amount: string, quote: TaxQuote, rates: readonly TaxRate[], money: (v: number) => string) {
    const { rate, taxed } = resolve(quote, rates);
    if (!taxed || amount.trim() === "" || !Number.isFinite(toNumber(amount))) return null;
    const tax = (rate?.percent ?? 0) / 100;
    const inc = taxed && quote.includesTax;
    return `= ${money(fromNet(toNet(toNumber(amount), inc, tax), !inc, tax))} ${basisLabel(!inc, rate)}`;
}

/**
 * A money field typed as quoted: `amount` is shown as `quote` says, the quote picked inside the
 * field. Picking another quote keeps the amount typed and reads it that way (`onQuote`), e.g. a
 * price typed "ex GST" that was really inc. Without tax rates there's nothing to pick.
 */
export function QuotedAmountField(
    props: Readonly<{
        label: string;
        amount: string;
        quote: TaxQuote;
        rates: readonly TaxRate[];
        disabled?: boolean;
        /** The quote can't be changed (e.g. a cost following a supplier price, quoted as that price is). */
        quoteLocked?: boolean;
        /** The amount can't be typed (e.g. a price worked out by its pricing), though how it's taxed can still be picked. */
        amountLocked?: boolean;
        placeholder?: string;
        hint?: ReactNode;
        /** No line under it unless there's something to say (its hint, a note, an error): not the amount the other way (a compact row). */
        quiet?: boolean;
        /** Its hint before the amount the other way, after a dot ("+$25.00 inc GST on its $60.91 cost · $22.73 ex GST"), not after it. */
        hintFirst?: boolean;
        /** Said under the field in place of its hint (e.g. a big change just saved, with Undo): where the eye already is. */
        note?: ReactNode;
        validate?: (value: string) => string | null;
        /** As TextField's: highlighted while a warning is about it; each new token takes the cursor to it. */
        warning?: boolean;
        focusToken?: number;
        /** As TextField's: each valid change given as it's typed (a draft's field). */
        live?: boolean;
        onSave: (typed: string) => Promise<unknown>;
        onQuote: (quote: TaxQuote) => Promise<unknown>;
    }>,
) {
    const fmt = useMoney();
    // Saving another quote failed: said under the field (the amount's own errors show there too).
    const [error, setError] = useState<string | null>(null);
    // The amount the other way as it's typed (not only once it's saved).
    const hint = (typed: string) => {
        if (error) return <span className="text-danger">{error}</span>;
        if (props.note) return props.note;
        if (props.quiet) return props.hint ?? null;
        // Two sentences when there's both ("= $10.91 ex GST. On a $55.37 cost.").
        const other = otherWay(typed, props.quote, props.rates, fmt.money);
        if (!other || !props.hint) return other ?? props.hint ?? null;
        if (props.hintFirst) {
            return (
                <>
                    {props.hint} · {other.replace(/^= /, "")}
                </>
            );
        }
        return (
            <>
                {other}. {props.hint}
            </>
        );
    };
    const pick = (q: TaxQuote) => {
        setError(null);
        props.onQuote(q).catch((err: Error) => setError(err.message));
    };
    return (
        <TextField
            label={props.label}
            value={props.amount}
            warning={props.warning}
            focusToken={props.focusToken}
            placeholder={props.placeholder}
            prefix={fmt.symbol}
            suffix={
                props.rates.length ? (
                    <QuotePicker rates={props.rates} quote={props.quote} label={`${props.label}: how it's taxed`} disabled={props.disabled || props.quoteLocked} onChange={pick} />
                ) : undefined
            }
            inputMode="decimal"
            disabled={props.disabled || props.amountLocked}
            validate={props.validate}
            hint={hint}
            live={props.live}
            onSave={props.onSave}
        />
    );
}

